import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import type { SearchResponse } from "@rua-watch/shared";
import { useSearch } from "../api/hooks";
import { formatInt } from "../lib/format";

// F6 — global command palette. Single overlay, three result groups
// (IPs / Domains / Senders). Keyboard-first: cmd/ctrl+K opens, Esc
// closes, arrow keys navigate, Enter selects.
//
// Rendered via portal so it floats above the rest of the layout
// without inheriting flex/scroll constraints from the sidebar shell.

interface FlatItem {
  group: "ips" | "domains" | "senders";
  key: string;            // unique within the list
  label: string;          // primary line
  sub?: string;           // secondary hint (PTR, classification, msg count)
  to: string;             // target route
}

export function SearchPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [raw, setRaw] = useState("");
  const debounced = useDebounced(raw, 200);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const search = useSearch(debounced);

  // Reset on close so the next open isn't pre-populated.
  useEffect(() => {
    if (!open) {
      setRaw("");
      setActiveIdx(0);
    } else {
      // autoFocus on the input. setTimeout because the portal node may
      // not be mounted yet on the same tick.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // ESC closes the palette globally regardless of where focus lives.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const flat = useMemo<FlatItem[]>(() => buildFlat(search.data), [search.data]);

  // Reset selection whenever the result set changes — otherwise an
  // active index from a previous query lingers and can highlight the
  // wrong row.
  useEffect(() => {
    setActiveIdx(0);
  }, [debounced]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      const target = flat[activeIdx];
      if (target) {
        navigate({ to: target.to });
        onClose();
      }
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:pt-24"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
    >
      <button
        type="button"
        aria-label="Close search"
        className="fixed inset-0 bg-slate-900/40 dark:bg-slate-950/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl">
        <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
          <SearchIcon />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search IPs, domains, senders…"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            onKeyDown={onKeyDown}
            className="flex-1 bg-transparent text-sm text-slate-900 dark:text-slate-100 outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <kbd className="hidden rounded border border-slate-300 dark:border-slate-700 px-1.5 py-0.5 font-sans text-[10px] text-slate-500 dark:text-slate-400 sm:inline">
            esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
          {raw.trim().length < 2 ? (
            <Hint>Type at least 2 characters to search.</Hint>
          ) : search.isLoading ? (
            <Hint>Searching…</Hint>
          ) : flat.length === 0 ? (
            <Hint>No matches for &ldquo;{raw}&rdquo;.</Hint>
          ) : (
            <ResultGroups
              data={search.data!}
              flat={flat}
              activeIdx={activeIdx}
              onActivate={(idx) => setActiveIdx(idx)}
              onSelect={(item) => {
                navigate({ to: item.to });
                onClose();
              }}
            />
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-[11px] text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-2">
            <KbdHint label="↑↓" /> navigate
            <KbdHint label="↵" /> open
            <KbdHint label="esc" /> close
          </div>
          <div>{flat.length} result{flat.length === 1 ? "" : "s"}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ResultGroups({
  data,
  flat,
  activeIdx,
  onActivate,
  onSelect,
}: {
  data: SearchResponse;
  flat: FlatItem[];
  activeIdx: number;
  onActivate: (idx: number) => void;
  onSelect: (item: FlatItem) => void;
}) {
  // We render groups in fixed order (IPs → Domains → Senders) and the
  // active index is computed against the flat list — so the keyboard
  // selection lines up with the rendered order.
  let cursor = 0;
  return (
    <ul role="listbox" className="py-1">
      {data.ips.length > 0 ? (
        <Group title="IPs">
          {data.ips.map((r) => {
            const idx = cursor++;
            const item = flat[idx];
            return (
              <Item
                key={`ip-${r.ip}`}
                active={idx === activeIdx}
                onMouseEnter={() => onActivate(idx)}
                onClick={() => onSelect(item)}
                primary={r.ip}
                secondary={r.ptr ?? r.classification ?? undefined}
              />
            );
          })}
        </Group>
      ) : null}

      {data.domains.length > 0 ? (
        <Group title="Domains">
          {data.domains.map((r) => {
            const idx = cursor++;
            const item = flat[idx];
            return (
              <Item
                key={`domain-${r.domain}`}
                active={idx === activeIdx}
                onMouseEnter={() => onActivate(idx)}
                onClick={() => onSelect(item)}
                primary={r.domain}
                secondary={r.rootDomain && r.rootDomain !== r.domain ? `under ${r.rootDomain}` : undefined}
              />
            );
          })}
        </Group>
      ) : null}

      {data.headerFroms.length > 0 ? (
        <Group title="Senders (header_from)">
          {data.headerFroms.map((r) => {
            const idx = cursor++;
            const item = flat[idx];
            return (
              <Item
                key={`sender-${r.headerFrom}`}
                active={idx === activeIdx}
                onMouseEnter={() => onActivate(idx)}
                onClick={() => onSelect(item)}
                primary={r.headerFrom}
                secondary={`${formatInt(r.messageCount)} messages · 30d`}
              />
            );
          })}
        </Group>
      ) : null}
    </ul>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <li>
      <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
        {title}
      </div>
      <ul>{children}</ul>
    </li>
  );
}

function Item({
  primary,
  secondary,
  active,
  onClick,
  onMouseEnter,
}: {
  primary: string;
  secondary?: string;
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        role="option"
        aria-selected={active}
        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left ${
          active
            ? "bg-slate-100 dark:bg-slate-800"
            : "hover:bg-slate-50 dark:hover:bg-slate-800/60"
        }`}
      >
        <span className="truncate font-mono text-sm text-slate-800 dark:text-slate-200">
          {primary}
        </span>
        {secondary ? (
          <span className="shrink-0 truncate text-xs text-slate-500 dark:text-slate-400">
            {secondary}
          </span>
        ) : null}
      </button>
    </li>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
      {children}
    </p>
  );
}

function KbdHint({ label }: { label: string }) {
  return (
    <kbd className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-1 font-sans text-[10px] text-slate-600 dark:text-slate-400">
      {label}
    </kbd>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// Flatten the grouped response into a single list so keyboard
// navigation has a stable index.
function buildFlat(data: SearchResponse | undefined): FlatItem[] {
  if (!data) return [];
  const out: FlatItem[] = [];
  for (const r of data.ips) {
    out.push({
      group: "ips",
      key: `ip-${r.ip}`,
      label: r.ip,
      sub: r.ptr ?? r.classification,
      to: `/ips/${encodeURIComponent(r.ip)}`,
    });
  }
  for (const r of data.domains) {
    // For monitored_domains rows we navigate to the canonical
    // /domains/$domain page. If the row IS a subdomain, the rollup
    // logic on the root page will surface it under the breakdown.
    out.push({
      group: "domains",
      key: `domain-${r.domain}`,
      label: r.domain,
      to: `/domains/${encodeURIComponent(r.domain)}`,
    });
  }
  for (const r of data.headerFroms) {
    // Send the user to the org root that hosts this sender — the
    // SubdomainBreakdownCard there shows the per-header_from row.
    const target = r.rootDomain ?? r.headerFrom;
    out.push({
      group: "senders",
      key: `sender-${r.headerFrom}`,
      label: r.headerFrom,
      sub: `${r.messageCount} messages · 30d`,
      to: `/domains/${encodeURIComponent(target)}`,
    });
  }
  return out;
}

// Tiny debounce hook. Avoids spamming the search endpoint on every
// keystroke; 200ms is short enough that typing-then-pause feels
// instant.
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}
